// Angular
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

// Material
import { MatSnackBar } from '@angular/material/snack-bar';

// localforage
import * as localforage from 'localforage';

// angulartics
import { Angulartics2 } from 'angulartics2';

// services
import { DrawerService } from '@app/services/drawer/drawer.service';
import { ApiService } from '@app/services/api/api.service';

// components
import { OpenHouseSelectComponent } from '@app/containers/open-house-select/open-house-select.component';

interface AnswerSet {
  response: string;
  active: boolean;
}

@Component({
  selector: 'oh-admin-feedback',
  templateUrl: './admin-feedback.component.html',
  styleUrls: ['./admin-feedback.component.scss'],
})
export class AdminFeedbackComponent implements OnInit {
  private token: any;
  private openHouse: any;
  public form: FormGroup;
  public question1: string;
  public question2: string;
  public question3: string;
  public answers1: AnswerSet[];
  public answers2: AnswerSet[];

  constructor(
    private fb: FormBuilder,
    private drawerService: DrawerService,
    private router: Router,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private angulartics2: Angulartics2,
  ) {
    this.form = this.fb.group({
      question1: ['', Validators.required],
      question2: ['', Validators.required],
      question3: [''],
    });

    this.question1 = 'How did the Open House app work today?';
    this.question2 = 'Do you plan to use the app again?';
    this.question3 = 'Care to provide us any feedback?';

    this.answers1 = [
      {
        response: 'Great',
        active: false,
      },
      {
        response: 'Fair',
        active: false,
      },
      {
        response: 'Poorly',
        active: false,
      },
    ];

    this.answers2 = [
      {
        response: 'Absolutely',
        active: false,
      },
      {
        response: 'Maybe',
        active: false,
      },
      {
        response: 'No',
        active: false,
      },
    ];
  }

  async ngOnInit() {
    this.token = await this.apiService.getTokenFromDatabase();
    this.openHouse = await localforage.getItem('oh-selected-open-house');
  }

  get valid() {
    return this.form.valid;
  }

  get value() {
    return this.form.value;
  }

  private formatFeedbackSubmission(submission: any) {
    const formattedSubmission = {
      open_id: parseInt(this.openHouse.open_id, 10),
      feedback_responses: [
        {
          question: this.question1,
          answer: submission.question1.response,
        },
        {
          question: this.question2,
          answer: submission.question2.response,
        },
        {
          question: this.question3,
          answer: submission.question3,
        },
      ],
    };
    return formattedSubmission;
  }

  private async saveFeedbackSubmission(submission: any) {
    submission = this.formatFeedbackSubmission(submission);

    if (navigator.onLine) {
      await this.apiService.postFeedback(this.token, submission);
      console.log('POSTed feedback to API: ', submission);
    } else {
      try {
        let submissions: any = await localforage.getItem('oh-feedback-submissions');
        if (!submissions) {
          submissions = [];
        }
        submissions = [...submissions, submission];
        try {
          await localforage.setItem('oh-feedback-submissions', submissions);
          console.log('Feedback saved to cache.');
        } catch (error) {
          console.error(error);
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  public select(question: string, selection: AnswerSet) {
    if (question === 'question1') {
      this.answers1 = this.answers1.map((answer: AnswerSet) => {
        answer.active = false;
        if (answer === selection) {
          answer.active = true;
        }
        return answer;
      });
      this.form.patchValue({
        question1: selection,
      });
    } else if (question === 'question2') {
      this.answers2 = this.answers2.map((answer: AnswerSet) => {
        answer.active = false;
        if (answer === selection) {
          answer.active = true;
        }
        return answer;
      });
      this.form.patchValue({
        question2: selection,
      });
    }
  }

  public async submit() {
    try {
      await this.saveFeedbackSubmission(this.form.value);
      this.router.navigate(['/welcome'], { queryParams: { drawer: 'admin-settings' } });
      this.snackBar.open('Thanks! Successfully submitted feedback.', null, { duration: 5000 });
      this.angulartics2.eventTrack.next({
        action: 'submitted',
        properties: { category: 'feedback', label: `open_id: ${this.openHouse.open_id} ~ online: ${navigator.onLine}` },
      });
    } catch (error) {
      this.angulartics2.eventTrack.next({
        action: 'displayedError',
        properties: { category: 'feedback', label: `open_id: ${this.openHouse.open_id} ~ online: ${navigator.onLine}` },
      });
      if (typeof error.error === 'string') {
        this.snackBar.open('Error submitting feedback: ', error.error, { duration: 15000 });
      } else {
        this.snackBar.open('Error submitting feedback: ', error.error.message, {
          duration: 15000,
        });
      }
    }
  }
}
